import React, { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useOrganization } from '../contexts/OrganizationContext'
import { experimentApi } from '../services/api'
import {
  Button,
  ConfirmDialog,
  DataTable,
  FormActions,
  FormField,
  FormSection,
  PageHeader,
  PageLayout,
  StatePanel,
  StatusBadge,
} from '../components/common'
import styles from './ExperimentsPage.module.css'

const UNDERSCORE_PATTERN = /_/g

function formatDate(str) {
  if (!str) return '-'
  const d = new Date(str)
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const DEFAULT_TREATMENT_CONFIG = {
  signal_weights: {
    follow_boost: 2.0,
    creator_affinity: 0.5,
    sport_affinity: 0.3,
    competition_affinity: 0.2,
    betting_style: 0.2,
  },
  diversity: {
    max_per_creator: 3,
    max_per_sport: 5,
  },
  confidence_threshold: 20,
}

function TreatmentConfigForm({ config, onChange }) {
  const sw = config.signal_weights || DEFAULT_TREATMENT_CONFIG.signal_weights
  const div = config.diversity || DEFAULT_TREATMENT_CONFIG.diversity
  const ct = config.confidence_threshold ?? DEFAULT_TREATMENT_CONFIG.confidence_threshold

  const updateSignalWeight = (key, value) => {
    onChange({
      ...config,
      signal_weights: { ...sw, [key]: parseFloat(value) || 0 },
    })
  }

  const updateDiversity = (key, value) => {
    onChange({
      ...config,
      diversity: { ...div, [key]: parseInt(value) || 0 },
    })
  }

  const signalFields = [
    { key: 'follow_boost', label: 'Follow Boost', defaultVal: 2.0 },
    { key: 'creator_affinity', label: 'Creator Affinity', defaultVal: 0.5 },
    { key: 'sport_affinity', label: 'Sport Affinity', defaultVal: 0.3 },
    { key: 'competition_affinity', label: 'Competition Affinity', defaultVal: 0.2 },
    { key: 'betting_style', label: 'Betting Style', defaultVal: 0.2 },
  ]

  return (
    <div className={styles.configSection}>
      <h4>Signal Weights</h4>
      <div className={styles.configGrid}>
        {signalFields.map(({ key, label, defaultVal }) => (
          <FormField
            key={key}
            label={label}
            name={`sw_${key}`}
            type="number"
            step="0.1"
            min={0}
            max={5}
            value={sw[key] ?? defaultVal}
            onChange={(e) => updateSignalWeight(key, e.target.value)}
            help={`Default: ${defaultVal}`}
          />
        ))}
      </div>

      <h4>Diversity</h4>
      <div className={styles.configGrid}>
        <FormField
          label="Max Per Creator"
          name="max_per_creator"
          type="number"
          min={1}
          max={20}
          value={div.max_per_creator ?? 3}
          onChange={(e) => updateDiversity('max_per_creator', e.target.value)}
          help="Default: 3"
        />
        <FormField
          label="Max Per Sport"
          name="max_per_sport"
          type="number"
          min={1}
          max={20}
          value={div.max_per_sport ?? 5}
          onChange={(e) => updateDiversity('max_per_sport', e.target.value)}
          help="Default: 5"
        />
      </div>

      <h4>Confidence</h4>
      <FormField
        label="Confidence Threshold"
        name="confidence_threshold"
        type="number"
        min={1}
        max={100}
        value={ct}
        onChange={(e) => onChange({ ...config, confidence_threshold: parseInt(e.target.value) || 20 })}
        help="Default: 20 — number of bets for full confidence"
      />
    </div>
  )
}

function TreatmentConfigSummary({ config, title = 'Treatment configuration', description }) {
  if (!config) return null
  const sw = config.signal_weights || {}
  const div = config.diversity || {}

  return (
    <div className={styles.configSummary}>
      <h4>{title}</h4>
      {description ? <p>{description}</p> : null}
      <dl>
        {Object.entries(sw).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k.replace(UNDERSCORE_PATTERN, ' ')}</dt>
            <dd>{v}</dd>
          </React.Fragment>
        ))}
        {Object.entries(div).map(([k, v]) => (
          <React.Fragment key={k}>
            <dt>{k.replace(UNDERSCORE_PATTERN, ' ')}</dt>
            <dd>{v}</dd>
          </React.Fragment>
        ))}
        {config.confidence_threshold != null && (
          <>
            <dt>confidence threshold</dt>
            <dd>{config.confidence_threshold}</dd>
          </>
        )}
      </dl>
    </div>
  )
}

export function ExperimentsPage({
  embedded = false,
  showCreateAction = false,
  createSignal = null,
  selectedExperimentId,
  onSelectedExperimentChange,
}) {
  const { currentOrg } = useOrganization()
  const slug = currentOrg?.slug
  const queryClient = useQueryClient()

  const [localSelectedExperiment, setLocalSelectedExperiment] = useState(null)
  const selectedExperiment = selectedExperimentId ?? localSelectedExperiment
  const setSelectedExperiment = (nextValue) => {
    if (selectedExperimentId == null) setLocalSelectedExperiment(nextValue)
    onSelectedExperimentChange?.(nextValue)
  }
  const [showCreate, setShowCreate] = useState(false)
  const [createForm, setCreateForm] = useState({ name: '', control_percentage: 50, treatment_config: { ...DEFAULT_TREATMENT_CONFIG, signal_weights: { ...DEFAULT_TREATMENT_CONFIG.signal_weights }, diversity: { ...DEFAULT_TREATMENT_CONFIG.diversity } } })
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState(null)

  useEffect(() => {
    if (createSignal) {
      setShowCreate(true)
    }
  }, [createSignal])

  // List experiments
  const { data: listData, isLoading, error: listError, refetch: refetchList } = useQuery({
    queryKey: ['experiments', slug],
    queryFn: () => experimentApi.list(slug),
    enabled: !!slug,
  })

  // Detail query
  const { data: detailData, isLoading: detailLoading, error: detailError, refetch: refetchDetail } = useQuery({
    queryKey: ['experiments', slug, selectedExperiment],
    queryFn: () => experimentApi.get(slug, selectedExperiment),
    enabled: !!slug && !!selectedExperiment,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['experiments', slug] })

  const createMutation = useMutation({
    mutationFn: (data) => experimentApi.create(slug, data),
    onSuccess: () => {
      invalidate()
      setShowCreate(false)
      setCreateForm({ name: '', control_percentage: 50, treatment_config: { ...DEFAULT_TREATMENT_CONFIG, signal_weights: { ...DEFAULT_TREATMENT_CONFIG.signal_weights }, diversity: { ...DEFAULT_TREATMENT_CONFIG.diversity } } })
    },
  })

  const startMutation = useMutation({
    mutationFn: (id) => experimentApi.start(slug, id),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['experiments', slug, selectedExperiment] })
    },
  })

  const completeMutation = useMutation({
    mutationFn: (id) => experimentApi.complete(slug, id),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['experiments', slug, selectedExperiment] })
    },
  })

  const cancelMutation = useMutation({
    mutationFn: (id) => experimentApi.cancel(slug, id),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['experiments', slug, selectedExperiment] })
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => experimentApi.update(slug, id, data),
    onSuccess: () => {
      invalidate()
      queryClient.invalidateQueries({ queryKey: ['experiments', slug, selectedExperiment] })
      setEditMode(false)
    },
  })

  const [editMode, setEditMode] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', control_percentage: 50, treatment_config: { ...DEFAULT_TREATMENT_CONFIG } })

  const experiments = listData?.items || []
  const canStart = listData?.can_start === true
  const experiment = detailData?.experiment
  const results = detailData?.results || []
  const mutationError = createMutation.error || updateMutation.error || startMutation.error || completeMutation.error || cancelMutation.error
  const confidenceThreshold = Number(experiment?.treatment_config?.confidence_threshold || DEFAULT_TREATMENT_CONFIG.confidence_threshold)
  const observedPlacements = results.reduce((total, row) => total + Number(row.placements || 0), 0)
  const observedVariants = new Set(results.map((row) => row.variant).filter(Boolean)).size
  const evidenceState = results.length === 0
    ? { status: 'default', label: 'No evidence', detail: 'No experiment observations have been recorded.' }
    : observedVariants < 2 || observedPlacements < confidenceThreshold
      ? {
        status: 'warning',
        label: 'Early evidence',
        detail: `${observedPlacements.toLocaleString()} of ${confidenceThreshold.toLocaleString()} target placed bets across ${observedVariants} variant${observedVariants === 1 ? '' : 's'}.`,
      }
      : {
        status: 'success',
        label: 'Ready for review',
        detail: `${observedPlacements.toLocaleString()} placed bets across ${observedVariants} variants meet the configured evidence threshold.`,
      }

  const confirmLifecycleAction = () => {
    if (!pendingLifecycleAction) return
    const { type, id } = pendingLifecycleAction
    setPendingLifecycleAction(null)
    if (type === 'start' && canStart) startMutation.mutate(id)
    else if (type === 'complete') completeMutation.mutate(id)
    else if (type === 'cancel') cancelMutation.mutate(id)
  }

  const renderPage = (content, actions = null) => {
    const availability = !canStart && !isLoading ? (
      <StatePanel state="warning" title="Experiments are unavailable for this feed" description="You can review past results and edit drafts. New experiments cannot be started." compact />
    ) : null
    if (embedded) {
      return (
        <div>
          {showCreateAction && actions ? <div className={styles.embeddedActions}>{actions}</div> : null}
          {availability}
          {content}
        </div>
      )
    }

    return (
      <PageLayout size="wide">
        <PageHeader title="Experiments" context="Feed / Growth" actions={actions} />
        {availability}
        {content}
      </PageLayout>
    )
  }

  const columns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge auto>{row.status}</StatusBadge>,
    },
    {
      key: 'control_percentage',
      label: 'Control %',
      render: (row) => `${row.control_percentage}%`,
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => formatDate(row.created_at),
    },
  ]

  const resultColumns = [
    { key: 'computed_date', label: 'Date' },
    { key: 'variant', label: 'Variant' },
    { key: 'impressions', label: 'Impressions', sortable: true },
    { key: 'copies', label: 'Copies', sortable: true },
    { key: 'placements', label: 'Placed bets', sortable: true },
    {
      key: 'copy_rate',
      label: 'Copy Rate',
      render: (row) => row.copy_rate != null ? `${(row.copy_rate * 100).toFixed(2)}%` : '-',
    },
  ]

  // Detail view
  if (selectedExperiment) {
    if (detailLoading) {
      return renderPage(<StatePanel state="loading" title="Loading experiment" />)
    }

    if (detailError) {
      return renderPage(<StatePanel state="error" title="Experiment could not load" description={detailError.message} onRetry={refetchDetail} />)
    }

    if (!experiment) {
      return renderPage(
        <>
          <button className={styles.backButton} onClick={() => setSelectedExperiment(null)}>
            &larr; Back to list
          </button>
          <StatePanel state="empty" title="Experiment not found" description="Return to the list and choose another experiment." />
        </>
      )
    }

    return renderPage(
      <>
        <div className={styles.experimentDetail}>
          <button className={styles.backButton} onClick={() => { setSelectedExperiment(null); setEditMode(false) }}>
            &larr; Back to list
          </button>

          {mutationError ? <StatePanel state="error" title="Experiment action failed" description={mutationError.message || String(mutationError)} compact /> : null}

          {editMode ? (
            <div className={styles.createForm}>
              <h3>Edit Experiment</h3>
              <FormSection>
                <FormField
                  label="Name"
                  name="edit_name"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  required
                />
                <FormField
                  label="Control Percentage"
                  name="edit_control_percentage"
                  type="number"
                  value={editForm.control_percentage}
                  onChange={(e) => setEditForm({ ...editForm, control_percentage: parseInt(e.target.value) || 50 })}
                  min={10}
                  max={90}
                  help="Percentage of users in control group"
                />
                <TreatmentConfigForm
                  config={editForm.treatment_config || { ...DEFAULT_TREATMENT_CONFIG }}
                  onChange={(tc) => setEditForm({ ...editForm, treatment_config: tc })}
                />
              </FormSection>
              <FormActions>
                <Button variant="secondary" onClick={() => setEditMode(false)}>Cancel</Button>
                <Button
                  onClick={() => updateMutation.mutate({ id: selectedExperiment, data: editForm })}
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending ? 'Saving...' : 'Save'}
                </Button>
              </FormActions>
            </div>
          ) : (
            <>
              <div className={styles.experimentInfo}>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Name</span>
                  <span className={styles.infoValue}>{experiment.name}</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Status</span>
                  <span className={styles.infoValue}><StatusBadge auto>{experiment.status}</StatusBadge></span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Control %</span>
                  <span className={styles.infoValue}>{experiment.control_percentage}%</span>
                </div>
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Created</span>
                  <span className={styles.infoValue}>{formatDate(experiment.created_at)}</span>
                </div>
                {experiment.started_at && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Started</span>
                    <span className={styles.infoValue}>{formatDate(experiment.started_at)}</span>
                  </div>
                )}
                {experiment.ended_at && (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Ended</span>
                    <span className={styles.infoValue}>{formatDate(experiment.ended_at)}</span>
                  </div>
                )}
              </div>

              <div className={styles.comparisonGrid}>
                <TreatmentConfigSummary
                  title="Control baseline"
                  description={experiment.baseline_config ? 'Snapshot captured when this test started.' : 'Current organization baseline.'}
                  config={experiment.baseline_config || currentOrg?.settings?.[experiment.kind] || {}}
                />
                <TreatmentConfigSummary
                  title="Treatment"
                  description={`${100 - Number(experiment.control_percentage || 0)}% of eligible users receive these settings.`}
                  config={experiment.treatment_config}
                />
              </div>

              <div className={styles.actionButtons}>
                {experiment.status === 'draft' && (
                  <>
                    <Button
                      onClick={() => {
                        setEditForm({ name: experiment.name, control_percentage: experiment.control_percentage, treatment_config: experiment.treatment_config || { ...DEFAULT_TREATMENT_CONFIG, signal_weights: { ...DEFAULT_TREATMENT_CONFIG.signal_weights }, diversity: { ...DEFAULT_TREATMENT_CONFIG.diversity } } })
                        setEditMode(true)
                      }}
                      variant="secondary"
                    >
                      Edit
                    </Button>
                    <Button
                      onClick={() => setPendingLifecycleAction({ type: 'start', id: selectedExperiment })}
                      disabled={!canStart || startMutation.isPending}
                      title={!canStart ? 'Experiments are unavailable for this feed' : undefined}
                    >
                      {startMutation.isPending ? 'Starting...' : 'Start Experiment'}
                    </Button>
                  </>
                )}
                {experiment.status === 'running' && (
                  <>
                    <Button
                      onClick={() => setPendingLifecycleAction({ type: 'complete', id: selectedExperiment })}
                      disabled={completeMutation.isPending}
                    >
                      {completeMutation.isPending ? 'Completing...' : 'Complete'}
                    </Button>
                    <Button
                      variant="danger"
                      onClick={() => setPendingLifecycleAction({ type: 'cancel', id: selectedExperiment })}
                      disabled={cancelMutation.isPending}
                    >
                      {cancelMutation.isPending ? 'Cancelling...' : 'Cancel'}
                    </Button>
                  </>
                )}
              </div>
            </>
          )}

          <div className={styles.resultsSection}>
            <h3>Results</h3>
            <div className={styles.evidenceStatus}>
              <StatusBadge status={evidenceState.status}>{evidenceState.label}</StatusBadge>
              <p>{evidenceState.detail}</p>
            </div>
            {results.length === 0 ? (
              <StatePanel state="empty" title="No results yet" description="Results are computed daily while the experiment is running." compact />
            ) : (
              <DataTable
                columns={resultColumns}
                data={results}
                paginate
                pageSize={25}
              />
            )}
          </div>
          <ConfirmDialog
            open={Boolean(pendingLifecycleAction)}
            title={pendingLifecycleAction?.type === 'start'
              ? 'Start experiment?'
              : pendingLifecycleAction?.type === 'complete'
                ? 'Complete experiment?'
                : 'Cancel experiment?'}
            message={pendingLifecycleAction?.type === 'start'
              ? 'This activates the treatment for eligible users and changes live ranking behavior.'
              : pendingLifecycleAction?.type === 'complete'
                ? 'This stops allocation and records the current results as the experiment outcome.'
                : 'This stops allocation without marking the experiment successful.'}
            confirmLabel={pendingLifecycleAction?.type === 'start'
              ? 'Start experiment'
              : pendingLifecycleAction?.type === 'complete'
                ? 'Complete experiment'
                : 'Cancel experiment'}
            isDestructive={pendingLifecycleAction?.type === 'cancel'}
            onCancel={() => setPendingLifecycleAction(null)}
            onConfirm={confirmLifecycleAction}
          />
        </div>
      </>
    )
  }

  // List view
  const createAction = (
    <Button onClick={() => setShowCreate(!showCreate)}>
      {showCreate ? 'Cancel' : 'New test'}
    </Button>
  )

  return renderPage(
    <>
      {showCreate && (
        <div className={styles.createForm}>
          <h3>New experiment</h3>
          {mutationError ? <StatePanel state="error" title="Experiment could not be created" description={mutationError.message || String(mutationError)} compact /> : null}
          <FormSection>
            <FormField
              label="Name"
              name="name"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              required
              placeholder="e.g. Personalized Feed v1"
            />
            <FormField
              label="Control Percentage"
              name="control_percentage"
              type="number"
              value={createForm.control_percentage}
              onChange={(e) => setCreateForm({ ...createForm, control_percentage: parseInt(e.target.value) || 50 })}
              min={10}
              max={90}
              help="Percentage of users in control group"
            />
            <TreatmentConfigForm
              config={createForm.treatment_config}
              onChange={(tc) => setCreateForm({ ...createForm, treatment_config: tc })}
            />
          </FormSection>
          <FormActions>
            <Button variant="secondary" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button
              onClick={() => createMutation.mutate(createForm)}
              disabled={createMutation.isPending || !createForm.name}
            >
              {createMutation.isPending ? 'Creating...' : 'Create'}
            </Button>
          </FormActions>
        </div>
      )}

      {!slug ? (
        <StatePanel state="restricted" title="Select an organization" description="Choose an organization before managing experiments." />
      ) : listError ? (
        <StatePanel state="error" title="Experiments could not load" description={listError.message} onRetry={refetchList} />
      ) : (
        <DataTable
          columns={columns}
          data={experiments}
          loading={isLoading}
          emptyMessage={<StatePanel state="empty" title="No experiments yet" description="Create a test when you have a ranking change to validate." compact />}
          onRowClick={(row) => setSelectedExperiment(row.id)}
          paginate
          pageSize={25}
          label="Experiments"
        />
      )}
    </>,
    createAction
  )
}

export default ExperimentsPage
